import { useContext, useEffect, useRef, useState } from 'react';
import Navigation from '../../../assets/icons/trvto_navigation.png';
import ReactLoading from 'react-loading';
import './backpack.css';
import { useLocation, useNavigate } from 'react-router-dom';
import TypingAnimation from '../../../components/typingAnimation/TypingAni';
import { UserAuthContext } from '../../../context/UserAuthContext';
import { debouncedHandleAutoComplete, handleAutoComplete } from '../handleAutcomplete';
import { getMyRecent } from '../../../api/analytics';

const BackPacking = ({ className, style, handleIsSearch, type = 'trip' }) => {
    const autoCompleteRef = useRef();
    const inputFo = useRef();

    const [searchField, setSearchField] = useState('');
    const [loading, setLoading] = useState(false);
    const [autoCompleteData, setAutoCompleteData] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [limit, setLimit] = useState(9);
    const [spanDis, setSpanDis] = useState(true);
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const { isAuthenticated } = useContext(UserAuthContext);

    const divStyle = !isAuthenticated && window.location.pathname === '/' ? { width: '100%' } : {};

    const tripText = ['Search Your Destination', 'Explore Popular Destinations', 'Find Your Next Adventure'];

    /*
     * Clear search field when leaving the trip search page
     */
    useEffect(() => {
        const currentPath = location.pathname;
        const isSearchPage = currentPath.startsWith('/search-result/trip');

        if (!isSearchPage) {
            setSearchField('');
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleScroll = () => {
            // Remove focus from search input
            if (inputFo.current) {
                inputFo.current.blur();
            }

            // Hide suggestions
            setAutoCompleteData([]);
            setShowAutocomplete(false);

            // Show placeholder / typing animation again
            setSpanDis(true);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    /*
     * Close autocomplete when clicking outside
     */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autoCompleteRef.current && !autoCompleteRef.current.contains(event.target)) {
                setAutoCompleteData([]);
                setShowAutocomplete(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const headerTypingAniStyle = {
        searchContainer: {
            top: 0,
            left: 0
        }
    };

    // Empty box: popular destinations + this user's recent searches in ONE
    // call (works for guests too via anonymousId). Older logged-in history
    // from analytics is used only if nothing is saved yet.
    const loadSuggestionsWithRecent = async () => {
        const limit = window.innerWidth < 768 ? 5 : 10;
        const catlim = window.innerWidth < 768 ? 2 : 4;
        let recent = [];

        await handleAutoComplete({
            type,
            value: '',
            withRecent: true,
            setAutoCompleteData,
            setRecentSearches: (list) => {
                recent = list || [];
                setRecentSearches(recent);
            },
            limit,
            catlim
        });

        if (!recent.length && isAuthenticated) {
            try {
                const result = await getMyRecent('trip');
                if (result?.data?.status) setRecentSearches(result?.data?.data?.recentSearches || []);
            } catch (error) {
                console.error('Recent searches API failed:', error);
            }
        }
    };

    const handleDestinationFocus = async () => {
        setShowAutocomplete(true);
        setSpanDis(false);

        if (!searchField.trim()) {
            loadSuggestionsWithRecent();
        }
    };

    /*
     * Enter key search
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch(searchField);
        }
    };

    /*
     * Autocomplete
     */
    const handleSearchType = (value) => {
        const limit = window.innerWidth < 768 ? 5 : 10;
        const catlim = window.innerWidth < 768 ? 2 : 4;

        setSearchField(value);

        if (!value.trim()) {
            /*
             * Input was cleared.
             * Show recent searches + popular destinations again.
             */
            loadSuggestionsWithRecent();

            return;
        }

        /*
         * User started typing.
         * Recent searches will be hidden.
         */
        setAutoCompleteData([]);

        debouncedHandleAutoComplete({
            type,
            value,
            setAutoCompleteData,
            limit,
            catlim
        });
    };

    const handleDestinationClick = () => {
        setSpanDis(false);

        if (inputFo.current) {
            inputFo.current.focus();
        }

        if (!searchField.trim()) {
            loadSuggestionsWithRecent();
        }
    };

    /*
     * Search / autocomplete selection
     */
    const handleSearch = (query) => {
        const valueToSearch = (query || searchField).trim();

        if (!valueToSearch) {
            return;
        }

        const searchLimit = 9;

        setLimit(searchLimit);
        setAutoCompleteData([]);
        setSearchField('');

        if (typeof handleIsSearch === 'function') {
            handleIsSearch(false);
        }

        navigate(`/search-result/trip/${encodeURIComponent(valueToSearch)}`, {
            state: {
                limit: searchLimit
            }
        });
    };

    /*
     * Handle recent search click
     */
    const handleRecentSearchClick = (recentSearch) => {
        const valueToSearch = recentSearch?.place || recentSearch?.destination || '';

        if (!valueToSearch) {
            return;
        }

        setShowAutocomplete(false);

        handleSearch(valueToSearch);
    };

    function handleSpan() {
        setSpanDis(false);

        if (inputFo.current) {
            inputFo.current.focus();
        }
    }

    /*
     * Show recent searches only when:
     * - user is logged in
     * - input is empty
     * - recent searches exist
     */
    const shouldShowRecentSearches = isAuthenticated && !searchField.trim() && recentSearches.length > 0;

    const recentSearchNames = new Set(
        recentSearches.map((item) => (item?.place || item?.destination || '').trim().toLowerCase()).filter(Boolean)
    );

    const filteredAutoCompleteData = autoCompleteData.filter(
        (item) => !recentSearchNames.has(item.trim().toLowerCase())
    );

    return (
        <div style={style} className={`holidaySearchWrapper alignCenter ${className}`}>
            <div className='alignCenter'>
                <img src={Navigation} alt='trvto\\_navigation' />

                <div style={divStyle} className='relative alignInputWidth'>
                    <input
                        ref={inputFo}
                        value={searchField}
                        onFocus={handleDestinationFocus}
                        onBlur={() => setSpanDis(true)}
                        onClick={() => {
                            setSpanDis(false);
                            setShowAutocomplete(true);
                        }}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => handleSearchType(e.target.value)}
                        className='holidaySearch'
                    />

                    {spanDis && !searchField && (
                        <TypingAnimation
                            texts={tripText}
                            handleSpan={handleSpan}
                            seConStyle={headerTypingAniStyle.searchContainer}
                        />
                    )}

                    {showAutocomplete && (recentSearches.length > 0 || autoCompleteData.length > 0) && (
                        <div ref={autoCompleteRef} className='autoCompleteCon'>
                            {!searchField.trim() && recentSearches.length > 0 && (
                                <>
                                    <div className='recentSearchHeader'>Recent searches</div>

                                    {recentSearches.map((item, index) => {
                                        const searchName = item?.place || item?.destination;

                                        if (!searchName) {
                                            return null;
                                        }

                                        return (
                                            <div
                                                key={`recent-${searchName}-${index}`}
                                                className='recentSearchItem'
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleRecentSearchClick(item);
                                                }}
                                            >
                                                <span className='recentSearchIcon'>🕘</span>

                                                <span>{searchName}</span>
                                            </div>
                                        );
                                    })}
                                </>
                            )}

                            {filteredAutoCompleteData.length > 0 && (
                                <>
                                    {filteredAutoCompleteData.map((item, index) => (
                                        <div key={`${item}-${index}`} onClick={() => handleSearch(item)}>
                                            {item}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <button type='button' className='hoilidayrSearchBtn' onClick={() => handleSearch(searchField)}>
                Search
            </button>

            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
        </div>
    );
};

export default BackPacking;
